import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { Cluster, ClusterEditParams, ClusterResponse } from '@cast/types';

import { getClustersCacheUpdaterFn, enhanceCluster } from 'utils/api-utils';

import {
  EXTERNAL_CLUSTERS_QUERY_KEY,
  getClusterQueryKey,
} from '@cast/shared-cluster-context-data-access';

export const useUpdateClusterMutation = (
  id: string
): UseMutationResult<Cluster, unknown, ClusterEditParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      return await apiClient.clusters
        .updateCluster(
          { clusterId: id, update: payload },
          {
            timeout: 60_000,
          }
        )
        .then(({ data }) => enhanceCluster(data as ClusterResponse));
    },
    onSuccess: (cluster) => {
      queryClient.setQueryData(getClusterQueryKey({ id: cluster.id }), cluster);

      // Update all matching cluster-list caches using predicate approach
      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === EXTERNAL_CLUSTERS_QUERY_KEY &&
            Array.isArray(query.state.data),
        },
        getClustersCacheUpdaterFn(cluster)
      );
    },
    onError: (error) => notify.error(parseApiError(error)),
  });
};
